import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import StorefrontHeader from "../components/StorefrontHeader";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { PLATFORMS } from "../lib/platforms";
import { ArrowRight, Sparkles, Heart, Truck, Shield } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1554325109-87755d48a3fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwZmFzaGlvbiUyMGFjY2Vzc29yaWVzJTIwZmxhdGxheXxlbnwwfHx8fDE3ODIwNDU3MTl8MA&ixlib=rb-4.1.0&q=85";
const CAT_IMGS = [
  { img: "https://images.pexels.com/photos/18941996/pexels-photo-18941996.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", label: "Home & Decor" },
  { img: "https://images.pexels.com/photos/7792648/pexels-photo-7792648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", label: "Fashion & Accessories" },
  { img: "https://images.unsplash.com/photo-1522726336270-3a0053210f06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwY29mZmVlJTIwZ2VhciUyMGxpZmVzdHlsZXxlbnwwfHx8fDE3ODIwNDU3MTl8MA&ixlib=rb-4.1.0&q=85", label: "Lifestyle & Tech" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products", { params: { limit: 9 } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#F8F8F7] text-[#0A0A0A]">
      <StorefrontHeader />

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8 pb-20">
        <div className="grid md:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="md:col-span-7">
            <p className="overline text-[#666]" data-testid="hero-overline">
              Issue №01 — Curated affiliate picks
            </p>
            <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-[8rem] leading-[0.92] mt-6">
              Four<br />
              platforms.<br />
              <span className="text-[#E53935]">One</span> taste.
            </h1>
            <p className="mt-8 max-w-md text-base text-[#333] leading-relaxed">
              Hand-picked products from <strong>Etsy</strong>, <strong>Amazon</strong>, <strong>Shopify</strong>, and <strong>Shop.com</strong>. No filler. No paid placements that don&apos;t deserve their spot.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" data-testid="cta-browse" className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-7 py-4 text-sm uppercase tracking-wider font-semibold hover:bg-[#E53935] transition-colors">
                Browse the Edit <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop?featured=true" data-testid="cta-featured" className="inline-flex items-center gap-2 border border-[#0A0A0A] px-7 py-4 text-sm uppercase tracking-wider font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors">
                Editor&apos;s picks
              </Link>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={HERO_IMG} alt="Curated" className="w-full h-full object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-[#F8F8F7] p-4 flex items-center justify-between">
                <div>
                  <p className="overline text-[#666]">Now reading</p>
                  <p className="font-display font-black text-lg leading-none mt-1">The Quiet Luxury Edit</p>
                </div>
                <Sparkles className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="mt-16 border-y border-black/10 py-5 overflow-hidden">
          <div className="flex marquee-track gap-16 whitespace-nowrap">
            {[...Array(2)].flatMap((_, i) =>
              PLATFORMS.map((p) => (
                <span key={`${p.key}-${i}`} className="font-display font-black tracking-tighter text-3xl flex items-center gap-16">
                  {p.label.toUpperCase()} <span className="w-2 h-2 bg-[#E53935] rounded-full" />
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-[#666]">Section 02</p>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">Shop by Category</h2>
          </div>
          <Link to="/shop" className="story-link hidden md:inline-block text-sm font-semibold uppercase tracking-wider">All Categories →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {CAT_IMGS.map((c, i) => (
            <Link
              key={i}
              to={`/shop?category=${encodeURIComponent(c.label)}`}
              data-testid={`category-${i}`}
              className="group relative aspect-[4/5] overflow-hidden grid-card"
            >
              <img src={c.img} alt={c.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="overline text-white/80">Category</p>
                <h3 className="font-display font-black text-3xl text-white tracking-tighter mt-2">{c.label}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS - BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-[#666]">Section 03</p>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">The Latest Edit</h2>
          </div>
          <Link to="/shop" data-testid="see-all-link" className="story-link text-sm font-semibold uppercase tracking-wider">See all →</Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-12 gap-6 lg:gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="md:col-span-4 aspect-[4/5] bg-[#EBEBEA] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid md:grid-cols-12 gap-6 lg:gap-8 auto-rows-auto">
            {products.slice(0, 9).map((p, i) => (
              <ProductCard key={p.id} product={p} span={i === 0 ? "hero" : i % 5 === 0 ? "wide" : "default"} />
            ))}
          </div>
        )}
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 border-t border-black/10">
        <div className="grid md:grid-cols-3 gap-12">
          {[
            { Icon: Heart, t: "Hand-Picked", d: "Every product is reviewed before it earns a spot." },
            { Icon: Truck, t: "Multi-Platform", d: "Etsy, Amazon, Shopify, Shop.com — all in one feed." },
            { Icon: Shield, t: "Transparent", d: "Affiliate links disclosed. We earn only if you love it." },
          ].map(({ Icon, t, d }, i) => (
            <div key={i} className="border-t border-[#0A0A0A] pt-6">
              <Icon className="w-7 h-7 text-[#E53935]" strokeWidth={1.5} />
              <h4 className="font-display font-black text-2xl tracking-tighter mt-4">{t}</h4>
              <p className="text-sm text-[#666] mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-black/20 p-12 text-center" data-testid="empty-products">
      <p className="overline text-[#666]">Inventory loading</p>
      <h3 className="font-display font-black text-3xl tracking-tighter mt-3">No picks live yet.</h3>
      <p className="mt-3 text-sm text-[#666] max-w-md mx-auto">
        Sign in to your dashboard to add the first affiliate products from Etsy, Amazon, Shopify and Shop.com.
      </p>
      <Link to="/login" className="inline-block mt-6 bg-[#0A0A0A] text-white px-6 py-3 text-sm uppercase tracking-wider font-semibold hover:bg-[#E53935]">
        Go to Dashboard
      </Link>
    </div>
  );
}
